// frontend/src/components/voice/CallHistoryTable.js

import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../../components/ui/tooltip';
import {
    Play,
    FileText,
    Search,
    PhoneIncoming,
    PhoneOutgoing,
    RefreshCw,
    AlertCircle,
} from 'lucide-react';
import { useCallHistory } from '../../hooks/useVoiceAPI';
import { formatDuration, formatPhoneNumber, formatDate } from '../../lib/utils';
import { useUiLanguage } from '../../context/UiLanguageContext';
import { formatVapiEndedReason } from '../../i18n/vapi-reasons';

const CallHistoryTable = ({ profileId, showTest = false }) => {
    const { lang, t } = useUiLanguage();
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [outcomeFilter, setOutcomeFilter] = useState('all');
    const [selectedCall, setSelectedCall] = useState(null);
    const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);

    const filters = React.useMemo(() => ({
        search: searchQuery,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        outcome: outcomeFilter !== 'all' ? outcomeFilter : undefined,
    }), [searchQuery, statusFilter, outcomeFilter]);

    const { calls, loading, refetch } = useCallHistory(profileId || 'default', filters, showTest);

    // [POLISH] Sort calls by date (newest first)
    const sortedCalls = React.useMemo(() => {
        return [...(calls || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }, [calls]);

    const [searchParams] = useSearchParams();
    const callIdParam = searchParams.get('call_id');

    React.useEffect(() => {
        if (callIdParam && calls && calls.length > 0) {
            const found = calls.find(c => String(c.id) === callIdParam || c.vapi_call_id === callIdParam);
            if (found) {
                setSelectedCall(found);
                setIsTranscriptOpen(true);
            }
        }
    }, [callIdParam, calls]);

    const callNotFoundInList = Boolean(
        callIdParam &&
        !loading &&
        Array.isArray(calls) &&
        !calls.some(c => String(c.id) === callIdParam || c.vapi_call_id === callIdParam)
    );

    const formatReason = (reason) => formatVapiEndedReason(reason, lang);

    const getStatusBadge = (status) => {
        if (!status) return <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>—</span>;
        const s = status.toLowerCase();
        const variantMap = {
            completed: 'success',
            missed: 'danger',
            voicemail: 'info',
            failed: 'danger',
            in_progress: 'warning',
        };
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Badge variant={variantMap[s] || 'secondary'} style={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                            {status}
                        </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                        {s === 'completed' ? 'Call completed successfully' :
                            s === 'missed' ? 'Call was missed' :
                                s === 'voicemail' ? 'Caller left a voicemail' :
                                    s === 'in_progress' ? 'Call is active' :
                                        'Call failed to connect'}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    };

    const getOutcomeBadge = (outcome) => {
        if (!outcome || outcome === 'None' || outcome === '-') {
            return <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>—</span>;
        }
        const o = outcome.toLowerCase();
        const variantMap = {
            booked: 'accent',
            transferred: 'info',
            hangup: 'secondary',
            voicemail: 'secondary',
            completed: 'success',
        };
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Badge variant={variantMap[o] || 'secondary'} style={{ textTransform: 'capitalize', fontSize: '0.75rem' }}>
                            {o}
                        </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                        {o === 'booked' ? 'Appointment booked successfully' :
                            o === 'transferred' ? 'Call transferred to attorney' :
                                o === 'voicemail' ? 'Caller reached voicemail' :
                                    o === 'completed' ? 'Call completed successfully' :
                                        'Caller hung up'}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    };

    const getSentimentBadge = (sentiment) => {
        if (!sentiment || sentiment === 'None' || sentiment === '-') {
            return <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>—</span>;
        }
        const s = sentiment.toLowerCase();
        const variantMap = {
            positive: 'success',
            neutral: 'secondary',
            negative: 'warning',
            angry: 'danger',
        };
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Badge variant={variantMap[s] || 'secondary'} style={{ textTransform: 'capitalize', fontSize: '0.75rem' }}>
                            {s}
                        </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                        {s === 'positive' ? 'Caller was satisfied' :
                            s === 'negative' ? 'Caller expressed dissatisfaction' :
                                s === 'angry' ? 'Caller was angry/upset' :
                                    'Caller was neutral'}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    };

    const handleViewTranscript = (call) => {
        setSelectedCall(call);
        setIsTranscriptOpen(true);
    };

    const handlePlayRecording = (call) => {
        setSelectedCall(call);
        setIsTranscriptOpen(true);
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-muted)' }}>
                Loading call history...
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {callNotFoundInList && (
                <div
                    data-testid="call-not-in-list-warning"
                    style={{
                        padding: '0.75rem 1rem',
                        backgroundColor: 'var(--accent-soft)',
                        border: '1px solid var(--accent-border)',
                        borderRadius: '0.5rem',
                        color: 'var(--accent-ink)',
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                    }}
                >
                    <AlertCircle size={16} style={{ flexShrink: 0 }} />
                    <span>{t('voice.history.call_not_in_list')}</span>
                </div>
            )}
            {/* Filters */}
            <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ position: 'relative' }}>
                        <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <Input
                            placeholder="Search by phone number..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ paddingLeft: '2.5rem' }}
                        />
                    </div>
                </div>
                <div style={{ width: '180px' }}>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger>
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Statuses</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="missed">Missed</SelectItem>
                            <SelectItem value="voicemail">Voicemail</SelectItem>
                            <SelectItem value="failed">Failed</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div style={{ width: '180px' }}>
                    <Select value={outcomeFilter} onValueChange={setOutcomeFilter}>
                        <SelectTrigger>
                            <SelectValue placeholder="Outcome" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Outcomes</SelectItem>
                            <SelectItem value="booked">Booked</SelectItem>
                            <SelectItem value="transferred">Transferred</SelectItem>
                            <SelectItem value="hangup">Hangup</SelectItem>
                            <SelectItem value="voicemail">Voicemail</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                {/* [POLISH] Refresh Button */}
                <Button variant="outline" size="sm" onClick={refetch}>
                    <RefreshCw size={16} className="mr-2" /> Refresh
                </Button>
            </div>

            {/* Table Container with full responsive horizontal scroll */}
            <div
                style={{
                    border: '1px solid var(--border)',
                    borderRadius: '0.75rem',
                    overflowX: 'auto',
                    backgroundColor: 'var(--surface-raised)',
                    boxShadow: 'var(--shadow-sm)'
                }}
            >
                <Table style={{ minWidth: '1150px', width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <TableHeader>
                        <TableRow style={{ backgroundColor: 'var(--surface-sunken)', borderBottom: '1px solid var(--border)' }}>
                            <TableHead style={{ minWidth: '130px', whiteSpace: 'nowrap', padding: '0.75rem 1rem', color: 'var(--text-muted)', fontWeight: 600 }}>Date & Time</TableHead>
                            <TableHead style={{ minWidth: '145px', whiteSpace: 'nowrap', padding: '0.75rem 1rem', color: 'var(--text-muted)', fontWeight: 600 }}>Caller</TableHead>
                            <TableHead style={{ width: '75px', textAlign: 'center', whiteSpace: 'nowrap', padding: '0.75rem 0.5rem', color: 'var(--text-muted)', fontWeight: 600 }}>Direction</TableHead>
                            <TableHead style={{ minWidth: '85px', whiteSpace: 'nowrap', padding: '0.75rem 1rem', color: 'var(--text-muted)', fontWeight: 600 }}>Duration</TableHead>
                            <TableHead style={{ minWidth: '75px', whiteSpace: 'nowrap', padding: '0.75rem 1rem', color: 'var(--text-muted)', fontWeight: 600 }}>Cost</TableHead>
                            <TableHead style={{ minWidth: '160px', whiteSpace: 'nowrap', padding: '0.75rem 1rem', color: 'var(--text-muted)', fontWeight: 600 }}>Reason</TableHead>
                            <TableHead style={{ minWidth: '110px', whiteSpace: 'nowrap', padding: '0.75rem 1rem', color: 'var(--text-muted)', fontWeight: 600 }}>Intent</TableHead>
                            <TableHead style={{ minWidth: '100px', whiteSpace: 'nowrap', padding: '0.75rem 1rem', color: 'var(--text-muted)', fontWeight: 600 }}>Sentiment</TableHead>
                            <TableHead style={{ minWidth: '115px', whiteSpace: 'nowrap', padding: '0.75rem 1rem', color: 'var(--text-muted)', fontWeight: 600 }}>Status</TableHead>
                            <TableHead style={{ minWidth: '115px', whiteSpace: 'nowrap', padding: '0.75rem 1rem', color: 'var(--text-muted)', fontWeight: 600 }}>Outcome</TableHead>
                            <TableHead style={{
                                minWidth: '95px',
                                textAlign: 'center',
                                whiteSpace: 'nowrap',
                                padding: '0.75rem 1rem',
                                color: 'var(--text-muted)',
                                fontWeight: 600,
                                position: 'sticky',
                                right: 0,
                                backgroundColor: 'var(--surface-sunken)',
                                borderLeft: '1px solid var(--border)',
                                boxShadow: '-2px 0 4px rgba(32, 35, 31, 0.04)'
                            }}>
                                Actions
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {sortedCalls && sortedCalls.length > 0 ? (
                            sortedCalls.map((call) => (
                                <TableRow key={call.id} style={{ borderBottom: '1px solid var(--surface-sunken)', transition: 'background-color 0.15s' }}>
                                    <TableCell style={{ fontWeight: '500', whiteSpace: 'nowrap', padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--text)' }}>
                                        {formatDate(call.created_at)}
                                    </TableCell>
                                    <TableCell style={{ whiteSpace: 'nowrap', padding: '0.75rem 1rem', fontFamily: 'var(--font-mono)', fontSize: '0.8125rem', color: 'var(--text)' }}>
                                        {formatPhoneNumber(call.caller_phone || call.caller_number)}
                                        {call.is_test && (
                                            <Badge variant="secondary" style={{ marginLeft: '0.375rem', fontSize: '0.625rem', textTransform: 'uppercase' }}>
                                                {lang === 'ru' ? 'Тест' : 'Test'}
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell style={{ textAlign: 'center', padding: '0.75rem 0.5rem' }}>
                                        {call.direction === 'inbound' ? (
                                            <span title="Inbound call" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0.25rem', borderRadius: 'var(--radius-pill)', backgroundColor: 'var(--success-soft)', color: 'var(--success)' }}>
                                                <PhoneIncoming size={14} />
                                            </span>
                                        ) : (
                                            <span title="Outbound call" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0.25rem', borderRadius: 'var(--radius-pill)', backgroundColor: 'var(--info-soft)', color: 'var(--info)' }}>
                                                <PhoneOutgoing size={14} />
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell style={{ whiteSpace: 'nowrap', padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                                        {formatDuration(call.duration_seconds)}
                                    </TableCell>
                                    <TableCell style={{
                                        whiteSpace: 'nowrap',
                                        padding: '0.75rem 1rem',
                                        fontSize: '0.8125rem',
                                        fontVariantNumeric: 'tabular-nums',
                                        color: (call.cost || 0) > 2.00 ? 'var(--danger)' : 'var(--text)',
                                        fontWeight: (call.cost || 0) > 2.00 ? '600' : 'normal'
                                    }}>
                                        ${(call.cost || 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell style={{ whiteSpace: 'nowrap', padding: '0.75rem 1rem' }}>
                                        <Badge variant="outline" style={{ fontSize: '0.6875rem', fontWeight: 'normal', color: 'var(--text-muted)', borderColor: 'var(--border)', backgroundColor: 'var(--surface)' }}>
                                            {formatReason(call.ended_reason)}
                                        </Badge>
                                    </TableCell>
                                    <TableCell style={{ whiteSpace: 'nowrap', padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                                        {call.lead_intent && call.lead_intent !== 'N/A' ? (
                                            <Badge variant="outline" style={{ fontSize: '0.75rem' }}>{call.lead_intent}</Badge>
                                        ) : (
                                            <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>—</span>
                                        )}
                                    </TableCell>
                                    <TableCell style={{ whiteSpace: 'nowrap', padding: '0.75rem 1rem' }}>
                                        {getSentimentBadge(call.sentiment)}
                                    </TableCell>
                                    <TableCell style={{ whiteSpace: 'nowrap', padding: '0.75rem 1rem' }}>
                                        {getStatusBadge(call.status)}
                                    </TableCell>
                                    <TableCell style={{ whiteSpace: 'nowrap', padding: '0.75rem 1rem' }}>
                                        {getOutcomeBadge(call.outcome)}
                                    </TableCell>
                                    <TableCell style={{
                                        textAlign: 'center',
                                        whiteSpace: 'nowrap',
                                        padding: '0.75rem 1rem',
                                        position: 'sticky',
                                        right: 0,
                                        backgroundColor: 'var(--surface-raised)',
                                        borderLeft: '1px solid var(--border)',
                                        boxShadow: '-2px 0 4px rgba(32, 35, 31, 0.04)'
                                    }}>
                                        <div style={{ display: 'flex', gap: '0.35rem', justifyContent: 'center' }}>
                                            {(call.vapi_call_id || call.recording_url || call.transcript_json) ? (
                                                <>
                                                    {(call.vapi_call_id || call.recording_url) && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handlePlayRecording(call)}
                                                            style={{ padding: '0.35rem 0.5rem', height: '2rem', color: 'var(--accent)' }}
                                                            title="Слушать аудиозапись"
                                                        >
                                                            <Play size={15} />
                                                        </Button>
                                                    )}
                                                    {call.transcript_json && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleViewTranscript(call)}
                                                            style={{ padding: '0.35rem 0.5rem', height: '2rem', color: 'var(--text)' }}
                                                            title="Посмотреть расшифровку звонка"
                                                        >
                                                            <FileText size={15} />
                                                        </Button>
                                                    )}
                                                </>
                                            ) : (
                                                <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>—</span>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={11} style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--text-muted)' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                                        <PhoneIncoming size={32} style={{ color: 'var(--text-muted)' }} />
                                        <p style={{ fontWeight: 600, color: 'var(--text)' }}>No calls found</p>
                                        <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Incoming and outgoing calls will appear here</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Transcript & Recording Dialog */}
            <Dialog open={isTranscriptOpen} onOpenChange={setIsTranscriptOpen}>
                <DialogContent style={{ maxWidth: '48rem', maxHeight: '85vh', overflowY: 'auto' }}>
                    <DialogHeader>
                        <DialogTitle>Call Details & Transcript</DialogTitle>
                        <DialogDescription>
                            {selectedCall && formatPhoneNumber(selectedCall.caller_phone || selectedCall.caller_number)} •{' '}
                            {selectedCall && formatDate(selectedCall.created_at)}
                            {selectedCall?.duration_seconds ? ` • ${formatDuration(selectedCall.duration_seconds)}` : ''}
                        </DialogDescription>
                    </DialogHeader>

                    {/* Audio Player if vapi_call_id or recording_url exists */}
                    {(selectedCall?.vapi_call_id || selectedCall?.recording_url) && (
                        <div style={{ marginTop: '0.75rem', padding: '0.75rem 1rem', backgroundColor: 'var(--text)', borderRadius: '0.5rem', color: 'var(--surface-raised)' }}>
                            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent)', display: 'block', marginBottom: '0.35rem' }}>
                                Audio Recording
                            </span>
                            <audio
                                controls
                                src={selectedCall?.id ? `/api/v1/voice/calls/${selectedCall.id}/recording` : selectedCall.recording_url}
                                style={{ width: '100%', height: '36px' }}
                                autoPlay
                            />
                        </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
                        {selectedCall?.transcript_json?.messages && selectedCall.transcript_json.messages.length > 0 ? (
                            selectedCall.transcript_json.messages.map((message, index) => (
                                <div
                                    key={index}
                                    style={{ display: 'flex', justifyContent: message.role === 'assistant' ? 'flex-start' : 'flex-end' }}
                                >
                                    <div
                                        style={{
                                            maxWidth: '80%',
                                            borderRadius: '0.5rem',
                                            padding: '0.75rem',
                                            backgroundColor: message.role === 'assistant' ? 'var(--accent-soft)' : 'var(--surface-sunken)',
                                            color: message.role === 'assistant' ? 'var(--accent-ink)' : 'var(--text)'
                                        }}
                                    >
                                        <div style={{ fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                                            {message.role === 'assistant' ? 'Sofia (AI Assistant)' : 'Caller'}
                                        </div>
                                        <p style={{ fontSize: '0.875rem', margin: 0 }}>{message.content}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', textAlign: 'center', padding: '2rem 0' }}>
                                Transcript not available for this call.
                            </p>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CallHistoryTable;
